'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Eye, MapPin, Truck, Users, ChevronUp, ChevronDown, Zap } from 'lucide-react';
import type { Job } from './JobManagementView';

interface JobTableProps {
  jobs: Job[];
  selectedRows: Set<string>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const priorityConfig = {
  standard: 'text-zinc-500',
  urgent: 'text-amber-400',
  critical: 'text-red-400',
};

const sourceConfig = {
  ServiceJockey: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  RigJockey: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  FleetJockey: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

type SortField = 'id' | 'client' | 'scheduledStart' | 'etaVariance' | 'crewCount';
type SortDir = 'asc' | 'desc';

export default function JobTable({ jobs, selectedRows, setSelectedRows }: JobTableProps) {
  const [sortField, setSortField] = useState<SortField>('scheduledStart');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const allIds = jobs.map((j) => j.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedRows.has(id));
  const someSelected = allIds.some((id) => selectedRows.has(id));

  const toggleAll = () => {
    if (allSelected) setSelectedRows(new Set());
    else setSelectedRows(new Set(allIds));
  };

  const toggleRow = (id: string) => {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRows(next);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = [...jobs].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'id') return a.id.localeCompare(b.id) * dir;
    if (sortField === 'client') return a.client.localeCompare(b.client) * dir;
    if (sortField === 'scheduledStart') return a.scheduledStart.localeCompare(b.scheduledStart) * dir;
    if (sortField === 'etaVariance') return (a.etaVariance - b.etaVariance) * dir;
    if (sortField === 'crewCount') return (a.crewCount - b.crewCount) * dir;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="inline-flex flex-col ml-1">
      <ChevronUp size={9} className={sortField === field && sortDir === 'asc' ? 'text-amber-400' : 'text-zinc-700'} />
      <ChevronDown size={9} className={sortField === field && sortDir === 'desc' ? 'text-amber-400' : 'text-zinc-700'} />
    </span>
  );

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-500/10 border-b border-amber-500/20 animate-slide-up flex-wrap">
          <span className="text-xs font-semibold text-amber-400">{selectedRows.size} selected</span>
          <div className="flex items-center gap-2 ml-2">
            <button className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md text-zinc-300 transition-all">Assign Assets</button>
            <button className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md text-zinc-300 transition-all">Export Selected</button>
          </div>
          <button onClick={() => setSelectedRows(new Set())} className="ml-auto text-xs text-zinc-500 hover:text-zinc-300">Clear</button>
        </div>
      )}

      {/* Mobile card view */}
      <div className="block md:hidden divide-y divide-zinc-800/50">
        {paginated.map((job) => (
          <div key={`mobile-${job.id}`} className="p-4 space-y-2.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="font-mono text-xs font-bold text-amber-400">{job.id}</span>
                <p className="text-sm font-medium text-zinc-200 mt-0.5">{job.client}</p>
                <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <MapPin size={10} /> {job.city}
                </p>
              </div>
              <StatusBadge status={job.status as any} />
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-zinc-400">{job.type}</span>
              <span className={`font-semibold capitalize ${priorityConfig[job.priority]}`}>
                {job.priority === 'critical' && '⚠ '}{job.priority}
              </span>
              {job.sourceApp && (
                <span className={`text-[10px] border rounded-full px-2 py-0.5 font-medium flex items-center gap-1 ${sourceConfig[job.sourceApp]}`}>
                  <Zap size={8} /> {job.sourceApp}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><Users size={11} /> {job.crewCount > 0 ? job.crewCount : '—'} crew</span>
              <span className="font-mono">{job.scheduledStart}</span>
              {job.etaVariance > 0 && <span className="text-red-400 font-mono">+{job.etaVariance}m late</span>}
            </div>
            {(job.assignedVehicles.length > 0 || job.assignedEquipment.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {job.assignedVehicles.map((v) => (
                  <span key={`mv-${job.id}-${v}`} className="flex items-center gap-0.5 text-[10px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">
                    <Truck size={8} /> {v}
                  </span>
                ))}
                {job.assignedEquipment.map((e) => (
                  <span key={`me-${job.id}-${e}`} className="text-[10px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">{e}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop table view */}
      <div className="hidden md:block overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/80">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                  className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('id')}>
                Job ID <SortIcon field="id" />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('client')}>
                Client <SortIcon field="client" />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Type</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Status</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Priority</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Assets</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('crewCount')}>
                Crew <SortIcon field="crewCount" />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300" onClick={() => handleSort('scheduledStart')}>
                Scheduled <SortIcon field="scheduledStart" />
              </th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Source</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">View</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {paginated.map((job) => {
              const isSelected = selectedRows.has(job.id);
              return (
                <tr
                  key={job.id}
                  className={`group transition-colors ${isSelected ? 'bg-amber-500/5' : 'hover:bg-zinc-800/40'}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRow(job.id)}
                      className="w-3.5 h-3.5 accent-amber-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-bold text-amber-400">{job.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-zinc-200 max-w-[160px] truncate">{job.client}</p>
                    <p className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5">
                      <MapPin size={9} /> {job.city}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{job.type}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status as any} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold capitalize ${priorityConfig[job.priority]}`}>
                      {job.priority === 'critical' && '⚠ '}{job.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {job.assignedVehicles.map((v) => (
                        <span key={`v-${job.id}-${v}`} className="flex items-center gap-0.5 text-[10px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">
                          <Truck size={8} /> {v}
                        </span>
                      ))}
                      {job.assignedEquipment.map((e) => (
                        <span key={`e-${job.id}-${e}`} className="text-[10px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">{e}</span>
                      ))}
                      {job.assignedVehicles.length === 0 && job.assignedEquipment.length === 0 && (
                        <span className="text-[10px] text-zinc-600 italic">Unassigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs text-zinc-400">
                      <Users size={11} /> {job.crewCount > 0 ? job.crewCount : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-mono text-zinc-300">{job.scheduledStart}</p>
                    {job.etaVariance > 0 && (
                      <p className="text-[10px] text-red-400 font-mono">+{job.etaVariance} min late</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {job.sourceApp && (
                      <span className={`text-[10px] border rounded-full px-2 py-0.5 font-medium flex items-center gap-1 w-fit ${sourceConfig[job.sourceApp]}`}>
                        <Zap size={8} /> {job.sourceApp}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button className="p-1.5 rounded-md text-zinc-500 hover:text-sky-400 hover:bg-sky-500/10 transition-all opacity-0 group-hover:opacity-100" title="View job details">
                      <Eye size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, sorted.length)} of {sorted.length} jobs</span>
        </div>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 text-xs rounded-md font-medium transition-all ${page === i + 1 ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}